import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RegistrationService } from '../../../services/registration.service';
import {
  EERegistrationInfo,
  EERegistrationVerificationOptions,
  RegistrationVerificationFields,
  VerificationQuestion,
  EERegistrationStatus
} from '../models/EERegistrationInfo';
import { Subject } from 'rxjs';
import { Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-registration-root',
  templateUrl: './registration-root.component.html',
  styleUrls: ['./registration-root.component.css'],
  standalone: false
})
export class RegistrationRootComponent implements OnInit {
  public steps = RegistrationSteps;
  public currentStep: RegistrationSteps = RegistrationSteps.Landing;
  public registrationRecord: EERegistrationInfo;
  public registrationVerificationOptions: EERegistrationVerificationOptions;
  public disableButton = true;
  public currentQuestion: Subject<VerificationQuestion>;
  public displayFailedValidationError = false;
  public isLoaded = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private registrationService: RegistrationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.currentQuestion = new Subject<VerificationQuestion>();
    let queryParams = this.activatedRoute.snapshot.queryParams;
    if (queryParams && queryParams.token) {
      // Fetch the recalculated registration data
      this.registrationService
        .getRecalculatedRegistrationDetailsByToken(queryParams.token)
        .subscribe(
          result => {
            this.registrationRecord = result;
            switch (this.registrationRecord.Status) {
              case EERegistrationStatus.Ineligible:
              case EERegistrationStatus.Locked:
                this.currentStep = RegistrationSteps.Failure;
                break;

              case EERegistrationStatus.Registered:
                this.currentStep = RegistrationSteps.RedirectingToLogin;
                setTimeout(() => {
                  this.authService.startAuthentication();
                }, 1000);
                break;

              default:
                if (this.registrationRecord.CanContinueRegistration) {
                  this.registrationService
                    .getRegistrationVerificationOptions(result.ClientCode, result.PersonId)
                    .subscribe(
                      opts => {
                        this.registrationVerificationOptions = opts;
                        this.disableButton = false;
                      },
                      err => (this.currentStep = RegistrationSteps.Failure)
                    );
                } //Registration cannot be completed
                else this.currentStep = RegistrationSteps.Failure;
                break;
            }
            this.isLoaded = true;
          },
          err => {
            this.currentStep = RegistrationSteps.Failure;
            this.isLoaded = true;
          }
        );
    } // We either have no query string or token so send to failure page
    else {
      this.currentStep = RegistrationSteps.Failure;
      this.isLoaded = true;
    }
  }

  public validationResult(verified: boolean) {
    this.displayFailedValidationError = false;
    if (this.currentStep == RegistrationSteps.Landing) {
      this.registrationService.startRegistration(this.registrationRecord.RegistrationId).subscribe(
        _ => {
          this._goToNextStep();
        },
        err => {
          this.currentStep = RegistrationSteps.Failure;
        }
      );
    } else {
      //we need to re-fetch the Registration Record in order to know what to do next.
      this.registrationService
        .getRegistrationDetailsById(this.registrationRecord.RegistrationId)
        .subscribe(result => {
          this.registrationRecord = result;
          if (!result.CanContinueRegistration)
            //Immediate failure state.
            this.currentStep = RegistrationSteps.Failure;
          else {
            //We can continue. Did the last verification pass?
            //If so, go to the next step.
            if (verified) this._goToNextStep();
            else this.displayFailedValidationError = true;
          }
        });
    }
  }

  private _goToNextStep() {
    switch (this.currentStep) {
      case RegistrationSteps.Landing:
        this.currentQuestion.next(
          new VerificationQuestion(
            'Please enter your email address.',
            RegistrationVerificationFields.Email,
            [Validators.email],
            [{ Key: 'email', Value: 'You must provide a valid email address.' }]
          )
        );
        this.currentStep = RegistrationSteps.Email;
        break;
      case RegistrationSteps.Email:
        this.currentQuestion.next(
          new VerificationQuestion(
            'Please enter the first 5 digits of your Social Security number.',
            RegistrationVerificationFields.SSN,
            [Validators.minLength(5), Validators.maxLength(5)],
            [
              { Key: 'minLength', Value: 'You must enter at least 5 digits.' },
              { Key: 'maxLength', Value: 'You must enter at most 5 digits.' }
            ],
            false,
            false,
            '000-00',
            '___-__'
          )
        );
        this.currentStep = RegistrationSteps.SSN;
        break;
      case RegistrationSteps.SSN:
        const next = this._getStepAfterSSN();
        this.currentQuestion.next(next.question);
        this.currentStep = next.step;
        break;
      case RegistrationSteps.DirectDeposit:
      case RegistrationSteps.StreetAddress:
      case RegistrationSteps.City:
      case RegistrationSteps.PostalCode:
      case RegistrationSteps.PhoneNumber:
      case RegistrationSteps.BirthDate:
        //If we get here, we've successfully validated on all points.
        this.registrationService
          .completeRegistrationVerification(this.registrationRecord.RegistrationId)
          .subscribe(
            result => {
              window.location.href = result.Uri;
            },
            err => {
              this.currentStep = RegistrationSteps.Failure;
            }
          );
        break;
      default:
        this.currentStep = RegistrationSteps.Failure;
        break;
    }
  }

  private _getStepAfterSSN(): { step: RegistrationSteps; question: VerificationQuestion } {
    if (this.registrationVerificationOptions.HasDirectDeposit) {
      return {
        step: RegistrationSteps.DirectDeposit,
        question: new VerificationQuestion(
          'Please enter the last five digits of your Direct Deposit Account Number.',
          RegistrationVerificationFields.DirectDeposit,
          [Validators.minLength(5), Validators.maxLength(5)],
          [
            { Key: 'minLength', Value: 'You must enter at least 5 digits.' },
            { Key: 'maxLength', Value: 'You must enter at most 5 digits.' }
          ]
        )
      };
    } else if (this.registrationVerificationOptions.HasBirthdate) {
      return {
        step: RegistrationSteps.BirthDate,
        question: new VerificationQuestion(
          'Please enter your birth date.',
          RegistrationVerificationFields.Birthdate,
          [],
          [],
          true
        )
      };
    } else if (this.registrationVerificationOptions.HasPhone) {
      return {
        step: RegistrationSteps.PhoneNumber,
        question: new VerificationQuestion(
          'Please enter your phone number.',
          RegistrationVerificationFields.Phone,
          [],
          [],
          false,
          true
        )
      };
    } else if (this.registrationVerificationOptions.HasStreetAddress) {
      return {
        step: RegistrationSteps.StreetAddress,
        question: new VerificationQuestion(
          'Please enter your Street Address.',
          RegistrationVerificationFields.StreetAddress
        )
      };
    } else if (this.registrationVerificationOptions.HasCity) {
      return {
        step: RegistrationSteps.City,
        question: new VerificationQuestion(
          'Please enter the City you live in.',
          RegistrationVerificationFields.City
        )
      };
    } else if (this.registrationVerificationOptions.HasState) {
      return {
        step: RegistrationSteps.State,
        question: new VerificationQuestion(
          'Please enter the State you live in.',
          RegistrationVerificationFields.State
        )
      };
    } else if (this.registrationVerificationOptions.HasPostalCode) {
      return {
        step: RegistrationSteps.PostalCode,
        question: new VerificationQuestion(
          'Please enter your Postal Code.',
          RegistrationVerificationFields.PostalCode
        )
      };
    } else {
      return {
        step: RegistrationSteps.Failure,
        question: null
      };
    }
  }
}

enum RegistrationSteps {
  Landing,
  Email,
  SSN,
  DirectDeposit,
  BirthDate,
  PhoneNumber,
  StreetAddress,
  City,
  State,
  PostalCode,
  Failure,
  RedirectingToLogin
}
